/**
 * Event registration — "Wie schrijf je in?" person stepper.
 * Turns the Formie `personen` repeater into a tabbed stepper: a strip of
 * numbered person chips, only the active person's row visible. Adding a person
 * (Formie's add button) appends a chip and switches to it; min 1 is enforced by
 * Formie. Attach with x-data="eventPersons()" on the form wrapper.
 *
 * Formie initializes the form (and renders the first repeater row) on its own
 * schedule, which can be after Alpine's init — so we observe the repeater
 * subtree and re-render whenever rows change, and prime a few frames after init.
 */
const PERSON_SVG = `<svg viewBox="0 0 22 23" fill="currentColor" aria-hidden="true"><path d="M10.6667 13.3333C14.3486 13.3333 17.3333 10.3486 17.3333 6.66667C17.3333 2.98477 14.3486 0 10.6667 0C6.98477 0 4 2.98477 4 6.66667C4 10.3486 6.98477 13.3333 10.6667 13.3333Z"/><path d="M21.3253 22.6667H0.00800578C5.77792e-06 22.54 0 22.42 0 22.308C0.03788 19.8312 1.1844 17.502 3.124 15.9614C5.25155 14.2267 7.92183 13.2963 10.6667 13.3333C13.4115 13.2963 16.0818 14.2267 18.2093 15.9614C20.1489 17.502 21.2955 19.8312 21.3333 22.308C21.3333 22.4267 21.3333 22.5467 21.3253 22.6653V22.6667Z"/></svg>`;

export function eventPersons() {
  return {
    active: 0,
    count: 0,

    init() {
      this.repeater = this.$root.querySelector('[data-field-handle="personen"]');
      if (!this.repeater) return;

      this.setupAddGuard();
      this.render();
      this.attachObserver();

      // Prime: catch the first row if Formie renders it just after init.
      [0, 50, 200, 500].forEach((t) => setTimeout(() => {
        this.attachObserver();
        this.syncAndRender();
      }, t));
    },

    // Observe ONLY row add/remove (childList on .fui-repeater-rows). The chip
    // strip lives outside that node and render() only toggles row styles, so
    // this never re-triggers itself.
    attachObserver() {
      const wrap = this.rowsWrap();
      if (!wrap || wrap === this.observedWrap) return;
      if (this.observer) this.observer.disconnect();
      this.observer = new MutationObserver(() => this.syncAndRender());
      this.observer.observe(wrap, { childList: true });
      this.observedWrap = wrap;
    },

    rowsWrap() {
      return this.repeater.querySelector(".fui-repeater-rows");
    },

    rows() {
      const wrap = this.rowsWrap();
      return wrap ? Array.from(wrap.querySelectorAll(":scope > .fui-repeater-row")) : [];
    },

    addBtn() {
      return this.repeater.querySelector(".fui-repeater-add-btn");
    },

    activeRow() {
      return this.rows()[this.active] || null;
    },

    // A person row is complete when every required field is filled and valid
    // (email format included, via the input's own constraint validation).
    requiredFields(row) {
      return row
        ? Array.from(row.querySelectorAll("input[required], textarea[required], select[required]"))
        : [];
    },

    isRowValid(row) {
      return this.requiredFields(row).every(
        (el) => el.value.trim() !== "" && el.checkValidity()
      );
    },

    // Dim the add button while the active person is incomplete. We use a class +
    // aria-disabled rather than the `disabled` attribute so the click still fires
    // and the guard below can surface which fields are missing.
    refreshAddBtn() {
      const btn = this.addBtn();
      if (!btn) return;
      const ok = this.isRowValid(this.activeRow());
      btn.classList.toggle("is-disabled", !ok);
      btn.setAttribute("aria-disabled", ok ? "false" : "true");
    },

    setupAddGuard() {
      const btn = this.addBtn();
      if (!btn || btn.dataset.guardBound) return;
      btn.dataset.guardBound = "1";

      // Capture phase so we run before Formie's own add handler and can veto it.
      btn.addEventListener(
        "click",
        (e) => {
          const row = this.activeRow();
          if (this.isRowValid(row)) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          // Trigger Formie's inline errors (blur) and land the user on the first gap.
          let first = null;
          this.requiredFields(row).forEach((el) => {
            if (el.value.trim() === "" || !el.checkValidity()) {
              el.dispatchEvent(new Event("blur", { bubbles: true }));
              if (!first) first = el;
            }
          });
          if (first) first.focus();
        },
        true
      );

      // Keep the dimmed state in sync as the user types.
      this.repeater.addEventListener("input", () => this.refreshAddBtn());
      this.repeater.addEventListener("change", () => this.refreshAddBtn());
    },

    syncAndRender() {
      const n = this.rows().length;
      if (n > this.count) this.active = n - 1; // jump to the freshly added person
      if (this.active > n - 1) this.active = Math.max(0, n - 1);
      this.count = n;
      this.render();
    },

    setActive(i) {
      this.active = i;
      this.render();
    },

    render() {
      const wrap = this.rowsWrap();
      if (!wrap) return;
      const rows = this.rows();

      // Chip strip, placed right above the rows.
      if (!this.chipStrip || !this.chipStrip.isConnected) {
        this.chipStrip = document.createElement("div");
        this.chipStrip.className = "persons-stepper";
      }
      if (this.chipStrip.previousElementSibling !== null || this.chipStrip.parentNode !== wrap.parentNode) {
        wrap.parentNode.insertBefore(this.chipStrip, wrap);
      }

      rows.forEach((row, i) => {
        row.style.display = i === this.active ? "" : "none";
      });

      this.chipStrip.innerHTML = "";
      rows.forEach((row, i) => {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "persons-chip" + (i === this.active ? " is-active" : "");
        chip.setAttribute("aria-label", `Persoon ${i + 1}`);
        chip.setAttribute("aria-pressed", i === this.active ? "true" : "false");
        chip.innerHTML = `${PERSON_SVG}<span class="persons-chip__num">${i + 1}</span>`;
        chip.addEventListener("click", () => this.setActive(i));
        this.chipStrip.appendChild(chip);
      });

      this.refreshAddBtn();
    },
  };
}
